import { type QueryKey } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { buildTagColorByName } from '@/features/shared/tags';
import { testsGridConfig } from '@/features/tests/grid/gridConfig';
import { useTagOptions } from '@/shared/hooks';
import { buildTestsColumns } from './testsColumns';
import { useTestsTableEditDialog } from './useTestsTableEditDialog';
import { useTestsTableMetricsModel } from './useTestsTableMetricsModel';
import { useTestsTableRowActions } from './useTestsTableRowActions';
import type { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import type { TestEditValues, TestsTableProps } from '@/features/tests/components/TestsTable.types';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import type { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { GridColDef } from '@/ui/primitives';
import type { MetricThresholdGroup, QueryResult, TestListItemDto } from '@flowqa/shared';

type TestsTableControllerInput = Pick<
  TestsTableProps,
  | 'disableLinks'
  | 'enableMetricsBatch'
  | 'fixedFilters'
  | 'includeSuiteInNameColumn'
  | 'initialFilterModel'
  | 'initialSortModel'
  | 'metricsEndDay'
  | 'metricsStartDay'
  | 'onRowsChange'
  | 'onStatusSelect'
  | 'persistKey'
  | 'queryFn'
  | 'queryKey'
  | 'showDeltas'
  | 'showSuiteLinks'
  | 'sparklineRangeDays'
  | 'syncStateToUrl'
  | 'tagOptionsOverride'
>;

type TestsTableController = {
  columns: Array<GridColDef<TestListItemDto>>;
  deleteLoading: boolean;
  deleteTarget: ReturnType<typeof useTestsTableRowActions>['deleteTarget'];
  editEditor: ReturnType<typeof useDetailEditEditor<string>>;
  editErrors: ReturnType<typeof useFormErrors<TestEditValues>>;
  editForm: ReturnType<typeof useDialogFormState<TestEditValues>>;
  editTarget: TestListItemDto | null;
  gridConfig: ReturnType<typeof testsGridConfig>;
  handleEditClose: () => void;
  handleEditSave: (input: { tags: string[]; colors: TagColor[] }) => Promise<void>;
  handleRowsChange: (rows: TestListItemDto[]) => void;
  isAuthenticated: boolean;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  queryKey: QueryKey;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  togglableColumnFields: string[];
};

export const useTestsTableController = ({
  disableLinks = false,
  enableMetricsBatch = true,
  fixedFilters,
  includeSuiteInNameColumn = true,
  initialFilterModel,
  initialSortModel,
  metricsEndDay,
  metricsStartDay,
  onRowsChange,
  onStatusSelect,
  persistKey,
  queryFn,
  queryKey,
  showDeltas = true,
  showSuiteLinks = true,
  sparklineRangeDays = 7,
  syncStateToUrl = false,
  tagOptionsOverride,
}: TestsTableControllerInput): TestsTableController => {
  const { session } = useAuth();
  const isAuthenticated = session !== null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const [visibleRows, setVisibleRows] = useState<TestListItemDto[]>([]);
  const {
    editEditor,
    editErrors,
    editForm,
    editTarget,
    handleEditClose,
    handleEditRow,
    handleEditSave,
  } = useTestsTableEditDialog();
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const {
    deleteTarget,
    deleteLoading,
    deleteRowPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
  } = useTestsTableRowActions({ selectedProjectId });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const {
    groupErroredWithFailed,
    preferFileNameOverDisplayName,
    resolveGroupErroredWithFailed,
    resolvePreferFileNameOverDisplayName,
  } = useProjectStatusPresentation();
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.tests]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const { metricSeriesByTestId } = useTestsTableMetricsModel({
    enableMetricsBatch,
    isAuthenticated,
    metricsEndDay,
    metricsStartDay,
    selectedOrganisationId,
    sparklineRangeDays,
    visibleRows,
  });
  const filterTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const columns = useMemo(
    () =>
      buildTestsColumns({
        selectedProjectId,
        groupErroredWithFailed,
        preferFileNameOverDisplayName,
        resolveGroupErroredWithFailed,
        resolvePreferFileNameOverDisplayName,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.tests,
        resolveThresholds,
        showSuiteLinks: showSuiteLinks && !disableLinks,
        combineNameAndSuite: true,
        includeSuiteInNameColumn,
        includeTagsInNameColumn: true,
        includeActionsColumn: true,
        disableColumnResizeHandles: false,
        latestAsMeta: true,
        includeLastExecutedColumn: false,
        disableLinks,
        showDeltas,
        metricSeriesByTestId,
        filterSuites: [],
        filterTags,
        onEditRow: handleEditRow,
        onDeleteRow,
        deleteRowPendingId,
        tagColorByName,
      }),
    [
      disableLinks,
      filterTags,
      handleEditRow,
      includeSuiteInNameColumn,
      metricSeriesByTestId,
      onDeleteRow,
      onStatusSelect,
      groupErroredWithFailed,
      preferFileNameOverDisplayName,
      resolveGroupErroredWithFailed,
      resolvePreferFileNameOverDisplayName,
      resolveThresholds,
      selectedProjectId,
      setSelectedProjectId,
      showDeltas,
      showSuiteLinks,
      tagColorByName,
      thresholds.tests,
      deleteRowPendingId,
    ],
  );
  const togglableColumnFields = useMemo(() => {
    const helperOnlyFields = new Set([
      'suiteId',
      'tags',
      'statusFilter',
      'latestRunDurationMs',
      'latestRunTimestamp',
    ]);
    return columns.map((column) => column.field).filter((field) => !helperOnlyFields.has(field));
  }, [columns]);
  const gridConfig = testsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'tests-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
    include: showSuiteLinks
      ? undefined
      : {
          latestRun: true,
          executionStatusCounts: true,
          executionSummaries: true,
          metrics: true,
          metricDeltas: showDeltas,
        },
  });
  const handleRowsChange = useCallback(
    (rows: TestListItemDto[]) => {
      setVisibleRows(rows);
      onRowsChange?.(rows);
    },
    [onRowsChange],
  );

  return {
    columns,
    deleteLoading,
    deleteTarget,
    editEditor,
    editErrors,
    editForm,
    editTarget,
    gridConfig,
    handleEditClose,
    handleEditSave,
    handleRowsChange,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: queryFn ?? gridConfig.queryFn,
    queryKey: queryKey ?? gridConfig.queryKey,
    selectedOrganisationId,
    selectedProjectId,
    togglableColumnFields,
  };
};
